import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Silent 5xx / network-error reporter. Fires-and-forgets to
// /api/errors/report so admins can see live production issues in the
// Admin → Error Log tab without needing the user to file a support
// ticket. Prevents an infinite loop by NEVER reporting failures of the
// report endpoint itself.
//
// Also normalises FastAPI validation errors: `response.data.detail` from a
// 422 is a LIST of {type, loc, msg, input, url} objects. Callers all over
// the codebase do `toast.error(err.response.data.detail || "...")` — passing
// that raw list into React or sonner triggered "Objects are not valid as a
// React child" (React error #31) in production, spamming the Error Log.
// Rewriting `detail` in-place here means every downstream call site keeps
// working with zero code changes.
function _formatValidationDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 shape — pick the human-readable pieces
    const parts = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          const msg = d.msg || d.message || "";
          return loc ? `${loc}: ${msg}`.trim() : msg;
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : "Please check the form and try again.";
  }
  if (detail && typeof detail === "object") {
    // Some backend routes return {detail: {message: "..."}} etc — surface any string field
    return detail.message || detail.msg || detail.error || JSON.stringify(detail);
  }
  return "";
}

api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    // Normalise FastAPI 422 detail BEFORE any downstream error handler
    // reads it. Runs on every rejection but is a no-op unless the payload
    // actually needs flattening — negligible cost.
    try {
      const data = err?.response?.data;
      if (data && typeof data === "object") {
        const flat = _formatValidationDetail(data.detail);
        if (flat) {
          if (data.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) data.detail_obj = data.detail;
          data.detail = flat;
        }
      }
    } catch { /* never let the normaliser crash */ }
    try {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      // Suppress reports when the browser has told us we're offline. Every
      // request will fail with "Network Error" during a WiFi/mobile signal
      // drop — the admin doesn't need 40 duplicate entries per lost coffee
      // shop connection. When the user comes back online, real 5xxs will
      // start being reported again.
      const online = (typeof navigator !== "undefined") ? navigator.onLine : true;
      // Skip reporting for 4xx (client errors — user tried something they
      // couldn't do) and for the report endpoint itself.
      const shouldReport = (
        (status === undefined || status >= 500)
        && !url.includes("/errors/report")
        && online
      );
      if (shouldReport) {
        // Raw fetch so a broken axios instance can still deliver the report
        fetch(`${API}/errors/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            kind: "axios",
            message: err?.message || "Network error",
            stack: null,
            page: typeof window !== "undefined" ? window.location.pathname : null,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            status: status ?? null,
            method: (err?.config?.method || "").toUpperCase() || null,
            api_path: url,
          }),
        }).catch(() => {});
      }
    } catch { /* never let the reporter crash */ }
    return Promise.reject(err);
  },
);

export default api;
